/**
 * Sector Dropdown Component
 *
 * Single-select dropdown with upward-opening panel.
 * Desktop: panel opens above input with 16px gap
 * Mobile: bottom drawer with overlay
 *
 * @package oska
 */

/**
 * SectorDropdown class handles all interactions for a single sector dropdown element
 */
class SectorDropdown {
  /**
   * @param {HTMLElement} element - The root .sector-dropdown element
   */
  constructor(element) {
    this.root = element;
    this.trigger = element.querySelector(".sector-dropdown__trigger");
    this.panel = element.querySelector(".sector-dropdown__panel");
    this.overlay = element.querySelector(".sector-dropdown__overlay");
    this.options = element.querySelectorAll(".sector-dropdown__option");
    this.closeBtn = element.querySelector(".sector-dropdown__close");
    this.clearBtn = element.querySelector(".sector-dropdown__clear");
    this.textEl = element.querySelector(".sector-dropdown__text");
    this.fade = element.querySelector(".sector-dropdown__fade");
    this.optionsList = element.querySelector(".sector-dropdown__options");

    this.isOpen = false;
    this.selectedValue = null;
    this.selectedLabel = null;
    this.selectedIndex = null;
    this.placeholderText = this.textEl?.textContent || "Vali valdkond";

    this.init();
  }

  /**
   * Initialize event listeners and state
   */
  init() {
    // Check for pre-selected option
    this.options.forEach((option) => {
      if (option.classList.contains("is-selected")) {
        this.selectedValue = option.dataset.value;
        this.selectedLabel = option.textContent.trim();
        this.selectedIndex =
          option.dataset.index !== undefined
            ? parseInt(option.dataset.index, 10)
            : null;
        this.updateTriggerText();
      }
    });

    // Trigger click - toggle panel
    this.trigger.addEventListener("click", (e) => {
      // Don't toggle if clicking the clear button
      if (e.target.closest(".sector-dropdown__clear")) {
        return;
      }
      this.toggle();
    });

    // Overlay click - close panel
    if (this.overlay) {
      this.overlay.addEventListener("click", () => this.close());
    }

    // Close button click
    if (this.closeBtn) {
      this.closeBtn.addEventListener("click", () => this.close());
    }

    // Option clicks - select and close
    this.options.forEach((option) => {
      option.addEventListener("click", () => this.selectOption(option));
    });

    // Clear button - deselect
    if (this.clearBtn) {
      this.clearBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.clearSelection();
      });
    }

    // Keyboard navigation
    this.root.addEventListener("keydown", (e) => this.handleKeydown(e));

    // Close on outside click
    document.addEventListener("click", (e) => {
      if (this.isOpen && !this.root.contains(e.target)) {
        this.close();
      }
    });

    // Close on escape
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.close();
        this.trigger.focus();
      }
    });

    // Hide fade gradient when options are scrolled to the end
    if (this.optionsList) {
      this.optionsList.addEventListener("scroll", () => this.checkScrollFade());
    }
  }

  /**
   * Toggle panel open/closed
   */
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open the panel
   */
  open() {
    this.isOpen = true;
    this.root.classList.add("is-open");
    this.trigger.setAttribute("aria-expanded", "true");

    // Prevent body scroll on mobile when drawer is open
    if (window.innerWidth < 768) {
      document.body.style.overflow = "hidden";
    }

    // Focus first option or selected option
    const selectedOption = this.panel.querySelector(
      ".sector-dropdown__option.is-selected",
    );
    const firstOption = this.panel.querySelector(".sector-dropdown__option");
    const focusTarget = selectedOption || firstOption;
    if (focusTarget) {
      focusTarget.focus();
    }

    // Check fade state after layout settles
    requestAnimationFrame(() => this.checkScrollFade());
  }

  /**
   * Close the panel
   */
  close() {
    this.isOpen = false;
    this.root.classList.remove("is-open");
    this.trigger.setAttribute("aria-expanded", "false");

    // Restore body scroll
    document.body.style.overflow = "";
  }

  /**
   * Check if options list is scrolled to the end and toggle fade visibility
   */
  checkScrollFade() {
    if (!this.optionsList || !this.panel) return;
    const { scrollTop, scrollHeight, clientHeight } = this.optionsList;
    const atEnd = scrollHeight - scrollTop - clientHeight < 8;
    this.panel.classList.toggle("is-scrolled-end", atEnd);
  }

  /**
   * Select an option
   * @param {HTMLElement} option - The option element to select
   */
  selectOption(option) {
    const value = option.dataset.value;
    const label = option.textContent.trim();
    const index =
      option.dataset.index !== undefined
        ? parseInt(option.dataset.index, 10)
        : null;

    // Clear previous selection
    this.options.forEach((opt) => {
      opt.classList.remove("is-selected");
      opt.setAttribute("aria-selected", "false");
    });

    // Set new selection
    this.selectedValue = value;
    this.selectedLabel = label;
    this.selectedIndex = index;
    option.classList.add("is-selected");
    option.setAttribute("aria-selected", "true");

    this.updateTriggerText();
    this.root.classList.add("has-selection");
    this.dispatchChangeEvent();
    this.dispatchHighlightEvent();
    this.close();
    this.trigger.focus();
  }

  /**
   * Clear selection
   * @param {boolean} silent - If true, don't dispatch the clear highlight event (prevents loops)
   */
  clearSelection(silent = false) {
    this.options.forEach((option) => {
      option.classList.remove("is-selected");
      option.setAttribute("aria-selected", "false");
    });

    this.selectedValue = null;
    this.selectedLabel = null;
    this.selectedIndex = null;
    this.root.classList.remove("has-selection");
    this.textEl.textContent = this.placeholderText;
    this.dispatchChangeEvent();

    // Only dispatch clear highlight if not silent (prevents infinite loops)
    if (!silent) {
      this.dispatchClearHighlightEvent();
    }
  }

  /**
   * Update trigger text with selected label
   */
  updateTriggerText() {
    if (this.textEl && this.selectedLabel) {
      this.textEl.textContent = this.selectedLabel;
    }
  }

  /**
   * Dispatch custom change event
   */
  dispatchChangeEvent() {
    const event = new CustomEvent("sector-dropdown:change", {
      detail: {
        value: this.selectedValue,
        label: this.selectedLabel,
        index: this.selectedIndex,
      },
      bubbles: true,
    });
    this.root.dispatchEvent(event);
  }

  /**
   * Dispatch highlight event to scrolling cards
   */
  dispatchHighlightEvent() {
    if (this.selectedIndex === null) return;

    document.dispatchEvent(
      new CustomEvent("scrolling-cards:highlight", {
        detail: { cardIndex: this.selectedIndex },
      }),
    );
  }

  /**
   * Dispatch clear highlight event to scrolling cards
   */
  dispatchClearHighlightEvent() {
    document.dispatchEvent(new CustomEvent("scrolling-cards:clear-highlight"));
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} e
   */
  handleKeydown(e) {
    if (!this.isOpen) {
      // Open on arrow down/up or space/enter when trigger is focused
      if (
        e.target === this.trigger &&
        (e.key === "ArrowDown" ||
          e.key === "ArrowUp" ||
          e.key === " " ||
          e.key === "Enter")
      ) {
        e.preventDefault();
        this.open();
      }
      return;
    }

    const focusedOption = document.activeElement;
    const optionsArray = Array.from(this.options);
    const currentIndex = optionsArray.indexOf(focusedOption);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (currentIndex < optionsArray.length - 1) {
          optionsArray[currentIndex + 1].focus();
        }
        break;

      case "ArrowUp":
        e.preventDefault();
        if (currentIndex > 0) {
          optionsArray[currentIndex - 1].focus();
        }
        break;

      case " ":
      case "Enter":
        e.preventDefault();
        if (focusedOption.classList.contains("sector-dropdown__option")) {
          this.selectOption(focusedOption);
        }
        break;

      case "Home":
        e.preventDefault();
        optionsArray[0]?.focus();
        break;

      case "End":
        e.preventDefault();
        optionsArray[optionsArray.length - 1]?.focus();
        break;

      case "Tab": {
        // Fully trap Tab within the options list.
        e.preventDefault();
        const first = optionsArray[0];
        const last = optionsArray[optionsArray.length - 1];

        if (e.shiftKey) {
          // Shift+Tab: previous option, or close if at first
          if (currentIndex <= 0) {
            this.close();
            this.trigger.focus();
          } else {
            optionsArray[currentIndex - 1].focus();
          }
        } else {
          // Tab: next option, or wrap to first if at last
          if (currentIndex >= optionsArray.length - 1) {
            first.focus();
          } else {
            optionsArray[currentIndex + 1].focus();
          }
        }
        break;
      }
    }
  }

  /**
   * Get currently selected value
   * @returns {string|null}
   */
  getSelectedValue() {
    return this.selectedValue;
  }

  /**
   * Get currently selected label
   * @returns {string|null}
   */
  getSelectedLabel() {
    return this.selectedLabel;
  }

  /**
   * Set selected value programmatically
   * @param {string} value - Value to select
   */
  setSelectedValue(value) {
    const option = Array.from(this.options).find(
      (opt) => opt.dataset.value === value,
    );
    if (option) {
      this.selectOption(option);
    }
  }
}

/**
 * Initialize all sector dropdown components on the page
 */
function initSectorDropdown() {
  const dropdowns = document.querySelectorAll("[data-sector-dropdown]");
  const instances = [];

  dropdowns.forEach((dropdown) => {
    instances.push(new SectorDropdown(dropdown));
  });

  // Listen for view filter changes to clear sector dropdown selection
  document.addEventListener("view-filter:change", (e) => {
    // Clear all sector dropdown selections when switching views
    // Silent because view-filter.js already handles the highlight clearing
    instances.forEach((instance) => {
      instance.clearSelection(true);
    });
  });

  // Listen for highlight cleared events (e.g., clicking overlay or user scrolling)
  document.addEventListener("scrolling-cards:highlight-cleared", () => {
    instances.forEach((instance) => {
      instance.clearSelection(true); // Silent to prevent loop
    });
  });

  return instances;
}

export default initSectorDropdown;
export { SectorDropdown };
